import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Sparkles, RefreshCw, ChevronRight } from "lucide-react";

/**
 * Motion-style AI Daily Brief banner.
 * Sits at the top of the dashboard. Cached server-side per (user, date).
 */
export default function DailyBrief() {
  const [brief, setBrief] = useState(null);
  const [busy, setBusy] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [locked, setLocked] = useState(false);

  const load = async () => {
    setBusy(true);
    try {
      const r = await api.get("/ai/daily-brief");
      setBrief(r.data);
    } catch (e) {
      if (e.response?.status === 403) setLocked(true);
      setBrief(null);
    } finally {
      setBusy(false);
    }
  };

  const refresh = async () => {
    setRefreshing(true);
    try {
      const r = await api.post("/ai/daily-brief/refresh");
      setBrief(r.data);
    } catch (e) {
      // noop
    } finally {
      setRefreshing(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  if (busy && !brief) {
    return (
      <div
        className="hud-frame p-5 flex items-center gap-3"
        data-testid="daily-brief-loading"
      >
        <Sparkles size={14} className="text-oud animate-pulse" />
        <span className="mono text-[11px] uppercase tracking-[0.22em] text-muted-foreground">
          Compiling today's brief…
        </span>
      </div>
    );
  }

  if (locked) {
    return (
      <div className="hud-frame p-5 flex items-center justify-between gap-4" data-testid="daily-brief-locked">
        <div className="flex items-center gap-3">
          <Sparkles size={14} className="text-oud" />
          <div>
            <div className="kpi-label">AI Daily Brief</div>
            <div className="text-sm text-muted-foreground mt-1">
              Claude reads your tasks, deals and streaks every morning and tells you what matters. Pro feature.
            </div>
          </div>
        </div>
        <a href="/billing" className="hud-btn hud-btn-primary whitespace-nowrap" data-testid="daily-brief-upgrade">
          UPGRADE TO PRO
        </a>
      </div>
    );
  }

  if (!brief) return null;

  return (
    <div
      className="hud-frame p-5 relative overflow-hidden"
      data-testid="daily-brief"
    >
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1 min-w-0">
          <div className="kpi-label flex items-center gap-2 mb-2">
            <Sparkles size={11} className="text-oud" /> Today's brief
            {brief.ai === false && (
              <span className="text-muted-foreground/70 mono text-[9px]">· offline</span>
            )}
          </div>
          <div className="display text-xl sm:text-2xl font-bold uppercase tracking-tight leading-tight">
            {brief.headline}
          </div>
          <p className="text-sm text-muted-foreground mt-2 leading-relaxed max-w-3xl">
            {brief.body}
          </p>
          {brief.top3 && brief.top3.length > 0 && (
            <ul className="mt-4 space-y-1.5">
              {brief.top3.map((line, i) => (
                <li
                  key={i}
                  className="flex items-start gap-2 text-sm"
                  data-testid={`daily-brief-top-${i}`}
                >
                  <ChevronRight size={12} className="text-oud mt-1 flex-shrink-0" />
                  <span>{line}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
        <button
          onClick={refresh}
          disabled={refreshing}
          className="hud-btn !text-[10px]"
          data-testid="daily-brief-refresh"
          title="Regenerate today's brief"
        >
          <RefreshCw size={11} className={refreshing ? "animate-spin" : ""} />
          {refreshing ? "..." : "REGEN"}
        </button>
      </div>
      <div className="hairline-oud mt-5" />
    </div>
  );
}
